import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Eye, EyeOff, Sparkles, ArrowRight, Mail, Lock } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSubmitting, setForgotSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/");
    } else {
      setError(res.error || "Login failed");
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail.trim().toLowerCase() });
      toast.success("Check your inbox", {
        description: "If that email is registered, we've sent a password-reset link.",
      });
      setForgotOpen(false);
      setForgotEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Request failed");
    } finally {
      setForgotSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 grid-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-md bg-zinc-900 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-zinc-900">
              Shipment Code Generator
            </h1>
            <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">
              Logistics Console
            </p>
          </div>
        </div>

        <Card className="border-zinc-200 shadow-sm">
          <form onSubmit={handleSubmit} className="p-7 flex flex-col gap-4">
            <div>
              <h2 className="text-lg font-semibold text-zinc-900">Sign in</h2>
              <p className="text-sm text-zinc-500 mt-1">
                Use the credentials provided by your administrator.
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500 flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11"
                required
                data-testid="login-email"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500 flex items-center gap-1.5">
                <Lock className="w-3.5 h-3.5" /> Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={show ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 pr-10"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  data-testid="login-toggle-password"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-zinc-500 hover:text-zinc-900"
                  tabIndex={-1}
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="h-11 bg-blue-700 hover:bg-blue-800 text-white"
              data-testid="login-submit"
            >
              {submitting ? "Signing in…" : "Sign in"}
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>

            <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="text-xs text-zinc-500 hover:text-blue-700 underline-offset-2 hover:underline self-center"
                  data-testid="login-forgot-link"
                >
                  Forgot password?
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Request password reset</DialogTitle>
                  <DialogDescription>
                    Enter your email; an administrator will be notified and will reset your password manually.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleForgot} className="flex flex-col gap-3 mt-2">
                  <p className="text-sm text-zinc-600">
                    An administrator will be notified and will reset your password.
                  </p>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    className="h-11"
                    data-testid="forgot-email-input"
                  />
                  <DialogFooter>
                    <Button
                      type="submit"
                      disabled={forgotSubmitting}
                      data-testid="forgot-submit"
                      className="bg-blue-700 hover:bg-blue-800 text-white"
                    >
                      {forgotSubmitting ? "Sending…" : "Send request"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </form>
        </Card>

        <p className="text-center text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-6">
          Default admin: admin@shipment.app
        </p>
      </div>
    </div>
  );
}
